import React, { useEffect, useMemo, useState } from "react";
import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { prettyDate } from "@/lib/dates";

const WEEK = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const STATUS_STYLE = {
  pendente: "border-amber-200 bg-amber-50 text-amber-800",
  confirmada: "border-emerald-200 bg-emerald-50 text-emerald-800",
  creditada: "border-purple-200 bg-purple-50 text-purple-800",
};
const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

function DayDetail({ date, list, spaces, fixed }) {
  const bySpace = spaces.map((sp) => ({ sp, fx: fixed.filter((b) => b.space_id === sp.id), items: list.filter((r) => r.space_id === sp.id).sort((a, b) => (a.hours?.[0] ?? 0) - (b.hours?.[0] ?? 0)) })).filter((x) => x.items.length || x.fx.length);
  return (
    <div data-testid="agenda-day-detail" className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
      <p className="font-display text-base font-bold text-slate-900">{prettyDate(date)} · {list.length} reserva(s){fixed.length ? ` · ${fixed.length} horário(s) fixo(s)` : ""}</p>
      {bySpace.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Sem reservas neste dia.</p> : (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {bySpace.map(({ sp, items, fx }) => (
            <div key={sp.id} data-testid={`agenda-col-${sp.id}`}>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">{sp.name}</p>
              <div className="space-y-2">
                {fx.map((b) => (
                  <div key={b.id} data-testid={`agenda-fixed-${b.id}`} className="rounded-xl border border-emerald-300 bg-emerald-100 px-3 py-2 text-sm text-emerald-900">
                    <div className="flex items-center justify-between gap-2"><span className="font-bold">{String(b.hour).padStart(2, "0")}:00 - {String((b.hour + 1) % 24).padStart(2, "0")}:00</span><span className="text-xs font-semibold">Horário fixo</span></div>
                    <p className="truncate">{b.label || "Mensalista"}</p>
                  </div>
                ))}
                {items.map((r) => (
                  <div key={r.id} data-testid={`agenda-slot-${r.id}`} className={`rounded-xl border px-3 py-2 text-sm ${STATUS_STYLE[r.status] || "border-slate-200 bg-slate-50 text-slate-700"}`}>
                    <div className="flex items-center justify-between gap-2"><span className="font-bold">{r.time_label}</span><span className="text-xs font-semibold capitalize">{r.status}</span></div>
                    <p className="truncate">{r.name || "—"} · {r.phone}</p>
                    {r.code && <p className="text-xs opacity-70">Código {r.code}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AgendaView() {
  const t = todayISO();
  const [month, setMonth] = useState({ y: Number(t.slice(0, 4)), m: Number(t.slice(5, 7)) - 1 });
  const [selected, setSelected] = useState(t);
  const [spaces, setSpaces] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    api.get("/admin/blocks").then((r) => setBlocks(Array.isArray(r.data) ? r.data : r.data.blocks || [])).catch(() => {});
  }, []);
  const fixedFor = (iso) => { const [y, m, d] = iso.split("-").map(Number); const wd = new Date(y, m - 1, d).getDay(); return blocks.filter((b) => b.weekday === wd); };

  useEffect(() => {
    api.get("/spaces").then((r) => setSpaces(r.data)).catch(() => {});
    api.get("/admin/reservations", { params: { filter: "todas", status: "todos", q: "" } }).then((r) => setReservations(r.data.reservations || [])).catch(() => {});
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    reservations.filter((r) => r.status !== "cancelada" && r.status !== "expirada").forEach((r) => { (map[r.date] = map[r.date] || []).push(r); });
    return map;
  }, [reservations]);

  const first = new Date(month.y, month.m, 1).getDay();
  const days = new Date(month.y, month.m + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const shift = (d) => setMonth(({ y, m }) => { const n = new Date(y, m + d, 1); return { y: n.getFullYear(), m: n.getMonth() }; });
  const label = new Date(month.y, month.m, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  return (
    <div className="mt-4" data-testid="agenda-view">
      <div className="flex items-center gap-2">
        <CalendarRange className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Agenda do mês</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">Dias em verde têm reservas ou horários fixos (mensalistas). Toque em um dia para ver os detalhes.</p>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <Button data-testid="agenda-prev-month" onClick={() => shift(-1)} size="sm" variant="outline" className="rounded-full"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-center">
            <p data-testid="agenda-month" className="font-display text-lg font-bold text-slate-900">{label.charAt(0).toUpperCase() + label.slice(1)}</p>
            <button data-testid="agenda-today" onClick={() => { setMonth({ y: Number(t.slice(0, 4)), m: Number(t.slice(5, 7)) - 1 }); setSelected(t); }} className="text-xs font-medium text-[#1B7A45] hover:underline">Ir para hoje</button>
          </div>
          <Button data-testid="agenda-next-month" onClick={() => shift(1)} size="sm" variant="outline" className="rounded-full"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="mx-auto mt-4 grid max-w-xl grid-cols-7 gap-1.5 text-center">
          {WEEK.map((w) => <span key={w} className="text-[11px] font-bold uppercase text-slate-400">{w}</span>)}
          {cells.map((d, i) => {
            if (!d) return <span key={`e${i}`} />;
            const iso = `${month.y}-${pad(month.m + 1)}-${pad(d)}`;
            const count = (byDate[iso]?.length || 0) + fixedFor(iso).length;
            const isSel = iso === selected;
            return (
              <button key={iso} data-testid={`agenda-day-${iso}`} onClick={() => { setSelected(iso); setTimeout(() => document.querySelector('[data-testid="agenda-day-detail"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
                className={`relative flex aspect-square flex-col items-center justify-center rounded-xl text-sm font-semibold transition-colors ${count ? "bg-[#1B7A45] text-white hover:bg-[#156337]" : "bg-slate-50 text-slate-700 hover:bg-slate-100"} ${isSel ? "ring-2 ring-[#E8611A] ring-offset-2" : ""} ${iso === t && !count ? "border border-[#1B7A45]" : ""}`}>
                {d}
                {count > 0 && <span className="text-[10px] font-bold opacity-80">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <DayDetail date={selected} list={byDate[selected] || []} spaces={spaces} fixed={fixedFor(selected)} />
    </div>
  );
}
